import { CaretUp, CaretDown } from '@phosphor-icons/react';
import { useTopOpponents, type TopOpponent } from '@/api/ratings';

/**
 * TopOpponentsList — top adversaires FFE de Léon (Stats Hub V2 PR #I).
 *
 * Cards compactes consommant `/users/me/ffe/opponents` (PR #160). Présente
 * pour chaque adversaire : nom, ELO FIDE (si dispo), parties jouées, score
 * cumulé + pourcentage, breakdown W/D/L, Δ ELO moyen avec flèche colorée.
 *
 * Empty state cohérent : pédagogiquement honnête, on incite à jouer plus de
 * tournois plutôt que de remplir avec des données factices.
 */

function formatScore(row: TopOpponent): string {
  return `${row.score.toFixed(1)} / ${row.games_played}`;
}

function formatPercentage(row: TopOpponent): string {
  if (row.games_played === 0) {
    return '0%';
  }
  const pct = (row.score / row.games_played) * 100;
  return `${Math.round(pct)}%`;
}

function DeltaBadge({ value }: { value: number | null }): JSX.Element {
  if (value === null) {
    return <span className="text-ink-300 font-mono tabular-nums text-body-sm">—</span>;
  }
  // Arrondi à 1 décimale pour lisibilité (la valeur backend est un AVG float).
  const rounded = Math.round(value * 10) / 10;
  if (rounded > 0) {
    return (
      <span
        className="inline-flex items-center gap-1 font-mono tabular-nums text-body-sm"
        style={{ color: 'var(--volt-500)' }}
      >
        <CaretUp size={12} weight="bold" aria-hidden="true" />+{rounded}
      </span>
    );
  }
  if (rounded < 0) {
    return (
      <span
        className="inline-flex items-center gap-1 font-mono tabular-nums text-body-sm"
        style={{ color: 'var(--coral-500)' }}
      >
        <CaretDown size={12} weight="bold" aria-hidden="true" />
        {rounded}
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 font-mono tabular-nums text-body-sm"
      style={{ color: 'var(--ink-300)' }}
    >
      0
    </span>
  );
}

function LoadingSkeleton(): JSX.Element {
  return (
    <ul className="flex flex-col gap-1.5" aria-label="Chargement des adversaires">
      {Array.from({ length: 4 }).map((_, i) => (
        <li
          key={i}
          className="h-14 rounded-md border border-ink-700 bg-ink-900 animate-pulse"
          aria-hidden="true"
        />
      ))}
    </ul>
  );
}

function EmptyState(): JSX.Element {
  return (
    <div className="text-center py-6 px-4 rounded-md border border-ink-700 bg-ink-900">
      <p className="text-paper-50 text-body m-0">
        Aucun adversaire récurrent encore (besoin de plus de tournois FFE).
      </p>
    </div>
  );
}

export function TopOpponentsList(): JSX.Element {
  const q = useTopOpponents(10);

  return (
    <div className="bg-ink-800 border border-ink-700 rounded-xl p-6 flex flex-col gap-3">
      <div>
        <h3 className="text-heading-3 text-paper-50 m-0">Adversaires fréquents</h3>
        <p className="text-body-sm text-ink-300 italic mt-1 m-0">
          Top 10 affrontés en tournois FFE.
        </p>
      </div>

      {q.isLoading && <LoadingSkeleton />}

      {q.isError && (
        <p role="alert" className="text-coral-500 text-body-sm">
          Impossible de charger les adversaires.
        </p>
      )}

      {q.data && q.data.opponents.length === 0 && <EmptyState />}

      {q.data && q.data.opponents.length > 0 && (
        <ul className="flex flex-col gap-2">
          {q.data.opponents.map((op) => (
            <li
              key={op.name}
              className="grid grid-cols-[1fr_auto] gap-3 items-center px-3 py-2 rounded-md border border-ink-700 bg-ink-900"
            >
              <div className="flex flex-col gap-0.5 min-w-0">
                <p className="text-paper-50 text-body m-0 truncate">
                  <span>{op.name}</span>
                  {op.fide_elo !== null && (
                    <span className="text-ink-300 font-mono tabular-nums ml-2">
                      ({op.fide_elo})
                    </span>
                  )}
                </p>
                <p className="text-ink-300 text-body-sm m-0 font-mono tabular-nums">
                  {op.games_played} {op.games_played > 1 ? 'parties' : 'partie'}
                  <span className="mx-2 text-ink-700">·</span>
                  {formatScore(op)} ({formatPercentage(op)})
                  <span className="mx-2 text-ink-700">·</span>
                  <span>
                    {op.wins}W {op.draws}D {op.losses}L
                  </span>
                </p>
              </div>
              <DeltaBadge value={op.elo_delta_avg} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
